import {
  prisma,
  RestrictionType,
  RestrictionScope,
  RestrictionTargetType,
} from "client";

export interface CreateRestrictionInput {
  type: RestrictionType;
  enabled: boolean;
  maxQuantity: number;
  scope: RestrictionScope;
  targetType?: RestrictionTargetType | null;
  targetId?: string | null;
  // Legacy support - will be converted to targetType/targetId
  ticketIds?: string[];
  ticketCategoryIds?: string[];
}

export interface UpdateRestrictionInput
  extends Partial<CreateRestrictionInput> {
  id?: string;
}

/**
 * Get all restrictions for an event
 */
export const getEventRestrictions = async (eventId: string) => {
  try {
    const restrictions = await prisma.eventRestriction.findMany({
      where: {
        eventId,
      },
      orderBy: {
        type: "asc",
      },
    });

    return restrictions;
  } catch (error: any) {
    console.error("Error fetching event restrictions:", error);
    throw error;
  }
};

/**
 * Get enabled restrictions for an event
 */
export const getEnabledEventRestrictions = async (eventId: string) => {
  try {
    const restrictions = await prisma.eventRestriction.findMany({
      where: {
        eventId,
        enabled: true,
      },
      
      orderBy: {
        type: "asc",
      },
    });

    return restrictions;
  } catch (error: any) {
    console.error("Error fetching enabled event restrictions:", error);
    throw error;
  }
};

/**
 * Upsert restrictions for an event (create or update)
 * This will replace all existing restrictions with the new ones
 */
export const upsertEventRestrictions = async (
  eventId: string,
  restrictions: CreateRestrictionInput[],
  userId: string
) => {
  try {
    // First, delete all existing restrictions for this event
    await prisma.eventRestriction.deleteMany({
      where: {
        eventId,
      },
    });

    // Then create new restrictions
    const createdRestrictions = await Promise.all(
      restrictions.map((restriction) => {
        // Convert legacy format to targetType/targetId if needed
        let targetType = restriction.targetType;
        let targetId = restriction.targetId;

        return prisma.eventRestriction.create({
          data: {
            eventId,
            type: restriction.type,
            enabled: restriction.enabled,
            maxQuantity: restriction.maxQuantity,
            scope: restriction.scope,
            targetType,
            targetId,
          },
        });
      })
    );

    return createdRestrictions;
  } catch (error: any) {
    console.error("Error upserting event restrictions:", error);
    throw error;
  }
};

/**
 * Create a single restriction
 */
export const createEventRestriction = async (
  eventId: string,
  data: CreateRestrictionInput
) => {
  try {
    // Convert legacy format to targetType/targetId if needed
    let targetType = data.targetType;
    let targetId = data.targetId;

    const restriction = await prisma.eventRestriction.create({
      data: {
        eventId,
        type: data.type,
        enabled: data.enabled,
        maxQuantity: data.maxQuantity,
        scope: data.scope,
        targetType,
        targetId,
      },
    });

    return restriction;
  } catch (error: any) {
    console.error("Error creating event restriction:", error);
    throw error;
  }
};

/**
 * Update a single restriction
 */
export const updateEventRestriction = async (
  restrictionId: string,
  data: UpdateRestrictionInput
) => {
  try {
    // Convert legacy format to targetType/targetId if needed
    let targetType = data.targetType;
    let targetId = data.targetId;

    const restriction = await prisma.eventRestriction.update({
      where: {
        id: restrictionId,
      },
      data: {
        ...(data.enabled !== undefined && { enabled: data.enabled }),
        ...(data.maxQuantity !== undefined && {
          maxQuantity: data.maxQuantity,
        }),
        ...(data.scope !== undefined && { scope: data.scope }),
        ...(targetType !== undefined && { targetType }),
        ...(targetId !== undefined && { targetId }),
      },
    });

    return restriction;
  } catch (error: any) {
    console.error("Error updating event restriction:", error);
    throw error;
  }
};

/**
 * Delete a restriction
 */
export const deleteEventRestriction = async (restrictionId: string) => {
  try {
    await prisma.eventRestriction.delete({
      where: {
        id: restrictionId,
      },
    });

    return true;
  } catch (error: any) {
    console.error("Error deleting event restriction:", error);
    throw error;
  }
};

export * from "./validation";
